import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// 앱 최초 로드 시점의 history idx를 전역으로 한 번만 기록
// 네이티브 레이어가 /some-page를 열면 React 부팅 과정에서 / → /some-page로 라우팅되어 idx가 이미 1이 됨
// 이 진입 시점 idx를 기준으로 삼아야 "앱 내에서 실제로 페이지를 이동했는지" 판별 가능
const APP_ENTRY_IDX = window.history.state?.idx ?? 0;

// 푸시 cold-start 진입 표식. React 마운트 전 네이티브가 큐에 넣은 푸시는 useDeepLink 가
// replace 로 이동하므로 history idx 만으로는 "네이티브 레이어가 직접 연 페이지"와 구분되지 않는다.
// 이 표식이 있는 history 항목에서만 홈 fallback 을 허용해야 레이어 닫기 UX 를 건드리지 않는다.
export const COLD_START_ENTRY_STATE = { coldStartEntry: true };

/**
 * 뒤로가기 훅
 * - React Router 히스토리가 있으면 navigate(-1)
 * - 히스토리가 없으면 (앱 스킴링크로 진입한 경우):
 *   - 네이티브 앱: 브릿지 호출하여 새창 닫기
 *   - 브라우저: window.close()
 *
 * 브릿지 주입은 페이지 로드 시점에 미리 폴링하여 확인하고,
 * goBack 호출 시에는 즉시 동작한다.
 *
 * @param {object}  [options]
 * @param {boolean} [options.fallbackToHome=false]
 *   히스토리가 없을 때 closeLayer/window.close 대신 홈(/)으로 replace 한다.
 *   채팅방처럼 "이전 화면 없으면 홈" 이 정책인 화면만 명시적으로 켠다 —
 *   cold-start 표식 유무(푸시 부트 큐 vs 네이티브 레이어 직접 오픈)를 구분하지 않는다.
 *   기본값(false)은 기존 동작 그대로다.
 */
const useGoBack = ({ fallbackToHome = false } = {}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const bridgeRef = useRef({ ios: false, android: false, checked: false });

    // 네이티브 브릿지(closeLayer) 주입을 폴링으로 감지
    // 브릿지가 WebView 로드 후 비동기로 주입될 수 있어 최대 2초간 100ms 간격으로 체크
    useEffect(() => {
        const userAgent = navigator.userAgent;
        const isIOS = userAgent.match(/iPhone/i) !== null;
        const isAndroid = userAgent.match(/Android/i) !== null;

        if (!isIOS && !isAndroid) {
            bridgeRef.current.checked = true;
            return;
        }

        let elapsed = 0;
        const maxWait = 2000;
        const interval = 100;

        const check = () => {
            if (isIOS && window.webkit?.messageHandlers?.closeLayer) {
                bridgeRef.current.ios = true;
            }
            if (isAndroid && window.Android?.closeLayer) {
                bridgeRef.current.android = true;
            }
            return bridgeRef.current.ios || bridgeRef.current.android;
        };

        if (check()) {
            bridgeRef.current.checked = true;
            return;
        }

        const timer = setInterval(() => {
            elapsed += interval;
            if (check() || elapsed >= maxWait) {
                clearInterval(timer);
                bridgeRef.current.checked = true;
            }
        }, interval);

        return () => clearInterval(timer);
    }, []);

    const goBack = () => {

        const historyState = window.history.state;
        const currentIdx = historyState?.idx ?? 0;
        // MediaViewer 가 열릴 때 쌓는 idx 없는 항목(src/components/common/MediaViewer.jsx 의 { mediaViewer: true }).
        // 라우터 이동으로 뷰어가 언마운트되면 이 항목이 history 에 남아, Back 으로 되돌아온 화면이 그 위에 복원된다.
        // 이 항목은 항상 기존 항목 위에 push 되므로 앞선 화면이 있다는 확실한 표식이다 — 다른 idx 없는 항목은 인정하지 않는다.
        const onViewerPushedEntry = historyState?.mediaViewer === true;

        // 현재 idx가 진입 시점보다 크면 앱 내에서 페이지 이동이 있었으므로 뒤로가기
        // 진입 시점과 같거나 작으면 더 이상 돌아갈 곳이 없으므로 홈 또는 네이티브 브릿지 처리
        // fallbackToHome 화면(채팅방)은 진입 idx 가 아니라 "앞선 history 항목이 있는가"(idx > 0) 로 본다.
        // 새로고침하면 브라우저가 history.state.idx 를 보존한 채 모듈이 다시 로드돼 APP_ENTRY_IDX 가
        // 현재 idx 로 잡히는데, 그러면 목록 → 방 → 새로고침 → Back 이 목록 대신 홈으로 가
        // "이전 화면 있으면 이전 화면" 계약을 어긴다. 옵션 없는 화면의 판정은 그대로다.
        // idx 가 NaN 이면 idx 없는 항목(MediaViewer 의 { mediaViewer: true }) 위에 router 가 push 한 것이다
        // (undefined + 1). push 자체가 앞선 항목의 증거이므로 역시 이전 화면으로 본다.
        // 그 뒤 Back 으로 뷰어 항목 자체에 복원된 경우(onViewerPushedEntry)도 마찬가지다.
        const canGoBack = fallbackToHome
            ? Number.isNaN(currentIdx) || currentIdx > 0 || onViewerPushedEntry
            : currentIdx > APP_ENTRY_IDX;
        if (canGoBack) {
            navigate(-1);
            return;
        }

        // 히스토리가 없고 푸시 cold-start 로 진입한 화면이면 → 홈으로 리다이렉트
        // (/community/104 등에서 뒤로가기 → 홈). main WebView 에는 닫을 레이어가 없어 closeLayer 가 무반응이다.
        // replace로 이동하여 히스토리가 쌓이지 않게 함 (홈에서 다시 뒤로가기 시 closeLayer 호출)
        // 표식 없는 진입(네이티브 레이어가 직접 연 페이지)은 아래 closeLayer 로 부모 화면에 돌아간다.
        // fallbackToHome 을 켠 화면(채팅방)은 표식과 무관하게 항상 홈이다.
        if ((fallbackToHome || location.state?.coldStartEntry) && location.pathname !== '/') {
            navigate('/', { replace: true });
            return;
        }

        // 더 이상 돌아갈 곳이 없음 → 네이티브 브릿지로 WebView 레이어 닫기
        const { ios, android } = bridgeRef.current;

        if (ios) {
            window.webkit.messageHandlers.closeLayer.postMessage(null);
            return;
        }
        if (android) {
            window.Android.closeLayer();
            return;
        }

        // 일반 브라우저 fallback
        window.close();

        // url로 직접 접근하여  window.close() 동작 안하는 경우 확인창 띄운 후 메인 이동
        setTimeout(() => {
          const confirm = window.confirm('더 이상 돌아갈 페이지가 없습니다. 홈화면으로 돌아갑니다');
          if(confirm){
            navigate('/', { replace: true });
          }

        }, 100);
    };

    return goBack;
};

export default useGoBack;
